import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, WritableSignal, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import {
  DialogBase,
  TiimeButtonModule,
  TiimeDialogModule,
  TiimeFormModule,
  TiimeInputContainerModule
} from 'tiime-material';

import { CommonDataApiService } from '@api-services/common-data-api.service';
import { CONTRACT_STATUS_LABEL } from '@constants/contract-status-label.constant';
import { ContractStatus } from '@gi/enums/contract-status.enum';
import { CodeAndLabelSelectComponent } from '@shared-selectors/code-and-label-select/code-and-label-select.component';

import { LossContractForm } from './loss-reason-form';
import { ReturnedUpdateContractStatusDialogData } from './returned-update-contract-status-dialog-data';
import { UpdateContractStatusDialogData } from './update-contract-status-dialog-data';

@Component({
  selector: 'app-update-contract-status-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TiimeDialogModule,
    TiimeButtonModule,
    TiimeInputContainerModule,
    TiimeFormModule,
    TextFieldModule,
    CodeAndLabelSelectComponent
  ],
  standalone: true,
  templateUrl: './update-contract-status-dialog.component.html',
  styleUrls: [
    '../../../core/bases/simple-dialog/simple-dialog.base.scss',
    './update-contract-status-dialog.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateContractStatusDialogComponent extends DialogBase<
  UpdateContractStatusDialogComponent,
  ReturnedUpdateContractStatusDialogData
> {
  readonly contractLossReasons$ = this.commonDataApiService.getContractLossReasons();
  readonly ContractStatus = ContractStatus;
  readonly confirmMessage: WritableSignal<string> = signal<string>('');
  readonly nextStatus: WritableSignal<ContractStatus> = signal<ContractStatus>(null);
  readonly lossForm = new LossContractForm();

  constructor(
    dialogRef: MatDialogRef<UpdateContractStatusDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: UpdateContractStatusDialogData,
    private readonly commonDataApiService: CommonDataApiService
  ) {
    super(dialogRef);
    this.init(data);
  }

  confirm(): void {
    if (this.nextStatus() !== ContractStatus.lost) {
      this.close({ status: this.nextStatus() });
      return;
    }

    if (this.lossForm.invalid) {
      this.lossForm.markAllAsTouched();
      return;
    }

    this.close({
      status: ContractStatus.lost,
      lossReason: this.lossForm.controls.lossReason.value,
      lossComment: this.lossForm.controls.lossComment.value ?? undefined
    });
  }

  private init(data: UpdateContractStatusDialogData): void {
    this.nextStatus.set(data.nextStatus);
    const currentStatusLabel = CONTRACT_STATUS_LABEL[data.currentStatus];
    const nextStatusLabel = CONTRACT_STATUS_LABEL[data.nextStatus];
    const message = `Êtes-vous sûr de vouloir passer de l'état "${currentStatusLabel}" à "${nextStatusLabel}"${
      data.saveMessage ? ` qui entraînera l'enregistrement de toutes les données présentes sur le contrat` : ''
    }${
      data.currentStatus === ContractStatus.sent
        ? ` ${data.saveMessage ? `ainsi que` : 'qui entraînera'} la désactivation du lien de signature`
        : ''
    } ?`;

    this.confirmMessage.set(message);
  }
}
